import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUserState, useUserDispatch } from "../context/user";
import { validateUserSession } from "../context/user/user.actions";
import {
  SESSION_CHECK_INTERVAL_MS,
  IDLE_GRACE_MS,
} from "../constants/sessionConstants";

export default function useSessionValidation() {
  const { currentUser, loading, loggingOut } = useUserState();
  const dispatch = useUserDispatch();
  const navigate = useNavigate();

  const lastActivity = useRef(Date.now());
  const intervalRef = useRef(null);

  /* ---------- track UI activity ---------- */
  useEffect(() => {
    const stamp = () => (lastActivity.current = Date.now());
    ["mousemove", "keydown", "click", "scroll"].forEach((evt) =>
      window.addEventListener(evt, stamp)
    );
    return () =>
      ["mousemove", "keydown", "click", "scroll"].forEach((evt) =>
        window.removeEventListener(evt, stamp)
      );
  }, []);

  /* ---------- session checker ---------- */
  const checkSession = useCallback(async () => {
    // Don't ping if no user, still loading, or logging out
    if (!currentUser || loading || loggingOut) return;

    // Don't ping if user has been idle too long
    if (Date.now() - lastActivity.current > IDLE_GRACE_MS) return;

    try {
      await validateUserSession(dispatch);
    } catch (error) {
      console.warn("Session validation failed, redirecting to login");
      const fullPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      navigate(`/?next=${encodeURIComponent(fullPath)}`, { replace: true });
    }
  }, [dispatch, navigate]);

  /* ---------- start/stop interval based on login state ---------- */
  useEffect(() => {
    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only start interval if we have a user and finished initial loading
    if (currentUser && !loading && !loggingOut) {
      intervalRef.current = setInterval(
        checkSession,
        SESSION_CHECK_INTERVAL_MS
      );
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentUser?.employee_id, loading, loggingOut, checkSession]);
}
